import { type FC } from 'react'
import { Dropdown } from 'react-bootstrap'
import { ActionButton } from '../../components/DataTable/ActionButton'
import { CustomDropdownItem } from '../../components/Dropdown/CustomDropdownItem'
import { ScopedBlock } from '../../components/ScopedBlock/ScopedBlock'
import { MissingScopeAction } from '../../components/ScopedBlock/models'
import { type InviteResponse } from '../../store/auth/auth'
import { type User } from '../../models/User'
import { type InviteRow, type MemberRow, type TeamRow } from './teamRows'

export interface TeamRowActionHandlers {
  userDeactivationEnabled: boolean
  onEditUser: (user: User) => void
  onDeactivateUser: (user: User) => void
  onDeleteUser: (user: User) => void
  onResendInvite: (invite: InviteResponse) => void
  onDeleteInvite: (invite: InviteResponse) => void
}

export interface TeamRowActionsProps extends TeamRowActionHandlers {
  row: TeamRow
}

const MemberRowActions: FC<TeamRowActionHandlers & { row: MemberRow }> = ({ row, ...handlers }) => {
  const canDeactivate = handlers.userDeactivationEnabled && row.status !== 'deactivated'
  return (
    <ActionButton>
      <ScopedBlock scope={{ scope: 'user:update', superScope: ['user:admin'] }} missingScopeAction={MissingScopeAction.DISABLE}>
        <Dropdown.Item as={CustomDropdownItem} onClick={() => handlers.onEditUser(row.member)}>
          <div className='d-flex gap-2 align-items-center py-1 px-2'>
            <i className='bi bi-pencil-square' />
            <span>Edit</span>
          </div>
        </Dropdown.Item>
      </ScopedBlock>
      {canDeactivate ? (
        <>
          <Dropdown.Divider className='my-0' />
          <ScopedBlock scope={{ scope: 'user:delete', superScope: ['user:admin'] }} missingScopeAction={MissingScopeAction.DISABLE}>
            <Dropdown.Item as={CustomDropdownItem} onClick={() => handlers.onDeactivateUser(row.member)}>
              <div className='d-flex gap-2 align-items-center py-1 px-2'>
                <i className='bi bi-dash-circle' />
                <div className='d-flex flex-column'>
                  <span>Deactivate</span>
                  <span className='paragraph-xsmall zocks-text-gray-600'>Keeps user data accessible</span>
                </div>
              </div>
            </Dropdown.Item>
          </ScopedBlock>
        </>
      ) : null}
      <ScopedBlock scope={{ scope: 'user:delete', superScope: ['user:admin'] }} missingScopeAction={MissingScopeAction.DISABLE}>
        <Dropdown.Item as={CustomDropdownItem} onClick={() => handlers.onDeleteUser(row.member)}>
          <div className='d-flex gap-2 align-items-center py-1 px-2'>
            <i className='bi bi-trash' />
            {canDeactivate ? (
              <div className='d-flex flex-column'>
                <span>Delete</span>
                <span className='paragraph-xsmall zocks-text-gray-600'>Removes user data permanently</span>
              </div>
            ) : (
              <span>Delete</span>
            )}
          </div>
        </Dropdown.Item>
      </ScopedBlock>
    </ActionButton>
  )
}

const InviteRowActions: FC<TeamRowActionHandlers & { row: InviteRow }> = ({ row, ...handlers }) => (
  <ActionButton>
    <Dropdown.Item as={CustomDropdownItem} onClick={() => handlers.onResendInvite(row.invite)}>
      Resend
    </Dropdown.Item>
    <Dropdown.Item as={CustomDropdownItem} onClick={() => handlers.onDeleteInvite(row.invite)}>
      Delete
    </Dropdown.Item>
  </ActionButton>
)

export const TeamRowActions: FC<TeamRowActionsProps> = ({ row, ...handlers }) => {
  if (row.kind === 'invite') {
    if (row.status === 'requested') {
      return null
    }
    return <InviteRowActions row={row} {...handlers} />
  }
  return <MemberRowActions row={row} {...handlers} />
}
